// Phase 6A — Static family maps for the parts-suggestion engine.
//
// SAFETY CONTRACT (do not relax):
//   1. FAMILY_COMPANION_PARTS is BUCKETED (repair / companion / supplies).
//      The UI must render the buckets as separate sub-sections — never blended.
//   2. DVI_PHRASE_ALLOWLIST is a hand-curated, exact-substring allowlist.
//      No regex, no token fan-out, no fuzzy inference. Anything not on the
//      allowlist emits NO suggestions (silent miss is acceptable; over-
//      suggestion is not).
//   3. normalizeJobCategory returns null for anything not confidently canonical.
//      The hook drops jobs that normalize to null — no generic fallback.
//
// Adding entries to either map is a code change, not a config surface.
// Keep it conservative.
//
// PHASE 6B ANTI-DRIFT CONTRACT (CRITICAL):
//   The MAP inside normalizeJobCategory() below is mirrored server-side in the
//   `parts_suggestion_family_map` lookup table. The Pensieve RPC
//   `get_job_part_cooccurrence` joins on that table for family normalization.
//
//   ANY change to the MAP keys here REQUIRES a paired migration that inserts
//   the same (raw_category, family) pairs into parts_suggestion_family_map.
//
//   The Vitest contract test in __tests__/familyMaps.contract.test.ts asserts
//   parity. CI will fail on drift. Do not bypass.

import type { SuggestionBucket, SuggestionCategory } from './types';

/** Canonical system-family keys. Keep in sync with DVI_PHRASE_ALLOWLIST and
 *  JOB_CATEGORY_FAMILY_MAP. */
export type CanonicalFamily =
  | 'brakes'
  | 'tires'
  | 'electrical_battery'
  | 'oil_service'
  | 'cooling'
  | 'hvac'
  | 'suspension'
  | 'drivetrain'
  | 'engine_perf';

export interface CompanionPart {
  label: string;
  category: SuggestionCategory;
}

export type FamilyBuckets = Record<SuggestionBucket, CompanionPart[]>;

/** Per-family companion catalog, split into 3 buckets.
 *  Repair = parts the technician is likely replacing in this system.
 *  Companion = parts often replaced alongside but not always.
 *  Supplies = consumables / shop supplies.
 *
 *  This is NOT a "likely fix" predictor — it is a system-family inventory
 *  to surface relevant items. The UI labels each bucket distinctly. */
export const FAMILY_COMPANION_PARTS: Record<CanonicalFamily, FamilyBuckets> = {
  brakes: {
    repair: [
      { label: 'Brake Pads', category: 'parts' },
      { label: 'Rotors', category: 'parts' },
    ],
    companion: [
      { label: 'Hardware Kit', category: 'parts' },
      { label: 'Caliper Slide Pin Kit', category: 'parts' },
    ],
    supplies: [
      { label: 'Brake Fluid', category: 'shop_supplies' },
      { label: 'Brake Cleaner', category: 'shop_supplies' },
    ],
  },
  tires: {
    repair: [
      { label: 'Tire', category: 'tires' },
    ],
    companion: [
      { label: 'TPMS Service Kit', category: 'parts' },
      { label: 'Valve Stems', category: 'parts' },
    ],
    supplies: [
      { label: 'Wheel Weights', category: 'shop_supplies' },
      { label: 'Tire Mounting Lube', category: 'shop_supplies' },
    ],
  },
  electrical_battery: {
    repair: [
      { label: 'Battery', category: 'parts' },
    ],
    companion: [
      { label: 'Battery Terminal Kit', category: 'parts' },
    ],
    supplies: [
      { label: 'Battery Terminal Protector', category: 'shop_supplies' },
    ],
  },
  oil_service: {
    repair: [
      { label: 'Oil Filter', category: 'parts' },
    ],
    companion: [
      { label: 'Drain Plug Gasket', category: 'parts' },
    ],
    supplies: [
      { label: 'Engine Oil', category: 'shop_supplies' },
    ],
  },
  cooling: {
    repair: [],
    companion: [
      { label: 'Radiator Cap', category: 'parts' },
      { label: 'Hose Clamps', category: 'parts' },
    ],
    supplies: [
      { label: 'Coolant', category: 'shop_supplies' },
    ],
  },
  hvac: {
    repair: [
      { label: 'Cabin Air Filter', category: 'parts' },
    ],
    companion: [],
    supplies: [],
  },
  suspension: {
    repair: [],
    companion: [
      { label: 'Sway Bar Link', category: 'parts' },
    ],
    supplies: [],
  },
  drivetrain: {
    repair: [],
    companion: [],
    supplies: [
      { label: 'Gear Oil', category: 'shop_supplies' },
    ],
  },
  engine_perf: {
    repair: [
      { label: 'Spark Plugs', category: 'parts' },
    ],
    companion: [
      { label: 'Ignition Coil', category: 'parts' },
    ],
    supplies: [],
  },
};

/** Curated allowlist for DVI text → family.
 *  EACH ENTRY is an exact case-insensitive substring match against
 *  finding.title || finding.summary || finding.condition.
 *  No regex. No token splitting. Adding an entry requires a code change. */
export const DVI_PHRASE_ALLOWLIST: ReadonlyArray<{
  phrase: string;
  family: CanonicalFamily;
}> = [
  // Brakes
  { phrase: 'brake pad', family: 'brakes' },
  { phrase: 'brake rotor', family: 'brakes' },
  { phrase: 'pad thickness', family: 'brakes' },
  { phrase: 'rotor thickness', family: 'brakes' },
  // Tires
  { phrase: 'tire tread', family: 'tires' },
  { phrase: 'tire wear', family: 'tires' },
  { phrase: 'tread depth', family: 'tires' },
  // Battery / electrical
  { phrase: 'battery test', family: 'electrical_battery' },
  { phrase: 'battery cca', family: 'electrical_battery' },
  { phrase: 'battery state of health', family: 'electrical_battery' },
  // Oil service
  { phrase: 'oil level', family: 'oil_service' },
  { phrase: 'oil filter', family: 'oil_service' },
  // Cooling
  { phrase: 'coolant level', family: 'cooling' },
  // HVAC
  { phrase: 'cabin air filter', family: 'hvac' },
  // Suspension
  { phrase: 'sway bar link', family: 'suspension' },
  // Engine perf
  { phrase: 'spark plug', family: 'engine_perf' },
];

/** Return the family for a DVI finding's free text, or null if no allowlist
 *  phrase is present. */
export function matchDviFamily(text: string | null | undefined): CanonicalFamily | null {
  if (!text) return null;
  const haystack = text.toLowerCase();
  for (const entry of DVI_PHRASE_ALLOWLIST) {
    if (haystack.includes(entry.phrase)) return entry.family;
  }
  return null;
}

/** Strict normalization of jobs.category strings to a canonical family.
 *  Returns null for anything ambiguous so the hook can SKIP that job entirely.
 *  This prevents category drift (e.g. "Custom Inspection Plus") from emitting
 *  generic suggestions. */
export function normalizeJobCategory(
  raw: string | null | undefined,
): CanonicalFamily | null {
  if (!raw) return null;
  const k = raw.trim().toLowerCase().replace(/[\s-/&]+/g, '_');
  if (!k) return null;

  // Explicit canonical map. Any value not present here returns null.
  const MAP: Record<string, CanonicalFamily> = {
    // brakes
    brakes: 'brakes',
    brake: 'brakes',
    brake_service: 'brakes',
    brake_repair: 'brakes',
    // tires
    tires: 'tires',
    tire: 'tires',
    tire_service: 'tires',
    // battery / starting & charging (battery-only subset)
    battery: 'electrical_battery',
    starting_charging: 'electrical_battery',
    // oil / maintenance lube
    oil_service: 'oil_service',
    oil_change: 'oil_service',
    lof: 'oil_service',
    // cooling
    cooling: 'cooling',
    coolant_service: 'cooling',
    // hvac
    hvac: 'hvac',
    heatingventilationac: 'hvac',
    heating_ventilation_a_c: 'hvac',
    // suspension / steering
    suspension: 'suspension',
    steering_suspension: 'suspension',
    // drivetrain
    drivetrain: 'drivetrain',
    // engine perf
    engine_performance: 'engine_perf',
    tune_up: 'engine_perf',
  };

  return MAP[k] ?? null;
}
